import { EffectCallback, MutableRefObject, RefCallback, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash.throttle";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { bridge } from "@multiweb/reactCore/bridge";
import { MouseButton, OnJavaScriptChange } from "@multiweb/types/react";
import { MODAL_ROOT_ID } from "../../constants";
import { doNothing } from "../constants";
import {
    initialIntersectionState,
    IntersectionOptions,
    IntersectionState,
    InViewHookResponse,
    observe,
    unobserve,
} from "../intersectionObserver";
import { isArrayShallowEqual } from "../isArrayEqual";

const { stopPropagationNative } = bridge;

const logger = LazyLoadingSectionLogger.create(LoggingSection.coreUI);

/** Zpoždění, které se využívá v hooku `useContentOverflows`. */
const CONTENT_OVERFLOW_THROTTLE_MS = 150;
/** Zpoďění použíte v hooku useElementScrollData pro zpoždění několika scroll eventu po sobě */
const SCROLL_INFO_THROTTLE_MS = 150;

/**
 * Zjistí, zda element přetéká rodiče.
 * @param element Element kontrolovaný na přetečení.
 * @param ignoreHeightChange Určuje, zda se má ignorovat přetečení na vertikální ose.
 * @param ignoreWidthChange Určuje, zda se má ignorovat přetečení na horizontální ose.
 */
const textOverflows = <T extends HTMLElement>(element: T, ignoreHeightChange?: boolean, ignoreWidthChange?: boolean): boolean => (
    (!ignoreHeightChange && element.offsetHeight < element.scrollHeight)
    || (!ignoreWidthChange && element.offsetWidth < element.scrollWidth)
);

/**
 * Hook pro zjištění, zda element přetéká rodiče.
 * @ref Reference na zkoumaný element.
 * @param ignoreHeightChange Určuje, zda se má ignorovat přetečení na vertikální ose.
 * @param ignoreWidthChange Určuje, zda se má ignorovat přetečení na horizontální ose.
 */
export const useContentOverflows = <T extends HTMLElement>(ref: RefObject<T>, ignoreHeightChange?: boolean, ignoreWidthChange?: boolean) => {
    const [contentOverflows, setContentOverflows] = useState(false);

    const callback = useCallback(() => {
        const element = ref.current;
        if (element) {
            setContentOverflows(textOverflows<T>(element, ignoreHeightChange, ignoreWidthChange));
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, ignoreHeightChange, ignoreWidthChange]);

    useThrottleResizeObserver(ref, callback, callback, CONTENT_OVERFLOW_THROTTLE_MS);

    return contentOverflows;
};

/**
 * Hook pro zakázání scrollování po stisknutí prostředního tlačítka.
 * Provádět funkcionalitu až na event onMouseDown není možné.
 * @param ref Objekt s referencí na element pro zakázání scrollování.
 */
export const usePreventMiddleButtonScroll = <T extends HTMLElement>(ref: RefObject<T>) => {
    useEffect(() => {
        if (ref.current) {
            ref.current.onmousedown = (event) => {
                return (event.button !== MouseButton.middleButton);
            };
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current]);
};

/**
 * Hook pro nastavení zaměření elementu podle hodnoty prop. Pro správnou funkci musí být cílový element zaměřitelný, to lze nastavit pomocí tabindex.
 *
 * @example Použití
 * import {createRef} from "react";
 * import {TAB_INDEX_FOCUSABLE_ONLY, useSetFocus} from "@multiweb/reactUtils";
 * const Component = ({isFocused}) => {
 *  const ref = createRef<HTMLElement>>();
 *  useSetFocus<HTMLElement>(isFocused, ref);
 *  return (
 *      <div ref={ref} tabIndex={TAB_INDEX_FOCUSABLE_ONLY} >Click on me!</div>
 *  );
 * }
 *
 * @param isFocused Hodnota prop nastavující zaměření. Pokud je true, nastaví zaměření. Pokud je false, nic nedělá.
 * @param ref Objekt s referencí na element pro zaměření.
 * @param customScroll Funkce umožňující přizpůsobení chování scrollování nadřazené scrollovatelné komponenty. Pokud je zadaná, potlačí se automatický scroll
 * @param selectAllText Příznak, zda se má po zaměření automaticky označit všechen text. Aplikovatelné pouze pro typ HTMLInputElement a HTMLTextAreaElement.
 * při získání zaměření.
 */
export const useSetFocus = <T extends HTMLElement>(isFocused: boolean, ref: RefObject<T>, customScroll?: (element: T) => void, selectAllText?: boolean) => {
    useEffect(() => {
        if (ref.current && isFocused) {
            const focusOptions = customScroll ? { preventScroll: true } : undefined;
            ref.current.focus(focusOptions);
            if (customScroll) {
                customScroll(ref.current);
            }
            if (selectAllText && (ref.current instanceof HTMLInputElement || ref.current instanceof HTMLTextAreaElement)) {
                ref.current.select();
            }
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, isFocused]);
};

/**
 * Hook sloužící pro oddálení vypnutí spínače. Po zapnutí (`value` na `true`) hook
 * instantně vrací `true`. Po vypnutí (`value` na `false`) vrací hook ještě po dobu
 * `delayMs` hodnotu `true`, po uplynutí doby již vrací `false`.
 *
 * @param value Hodnota požadavku na stav vypínače.
 * @param delayMs Doba oddálení vypnutí spínače v milisekundách.
 * */
export const useTurnOffDelay = (value: boolean, delayMs: number) => {
    const [isTurnedOn, setIsTurnedOn] = useState(value);

    useEffect(() => {
        let timeoutId: number;
        if (!value) {
            timeoutId = window.setTimeout(() => {
                setIsTurnedOn(value);
            }, delayMs);
        } else {
            setIsTurnedOn(value);
        }
        return () => clearTimeout(timeoutId);
    }, [value, delayMs]);

    return isTurnedOn;
};

/**
 * Hook pro uchování předchozí hodnoty.
 *
 * @param value Hodnota pro uchování
 */
export const usePrevious = (value: any) => {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
};

/**
 * Hook slouží pro uchování hodnoty s nastavením počátečního stavu. Hook má uplatnění například v případě,
 * kdy chceme inicializovat hodnotu pro následnou animaci na hodnotu přicházející z props pomocí property transition.
 * @see AnimatedWidth
 *
 * @param value Uložená hodnota
 * @param initialValue Počáteční hodnota
 */
export const useInitialValue = <T,>(value: T, initialValue: T) => {
    const [internalValue, setInternalValue] = useState<T>(initialValue);

    useEffect(() => {
        setInternalValue(value);
    }, [value]);

    return internalValue;
};

/**
 * Hook nahrazující funkci setTimeout.
 *
 * @param callback Callback volaný po vypršení času
 * @param delayMs Doba zpoždění zavolání callback funkce
 */
export const useTimeout = (callback: () => void, delayMs: number) => {
    const savedCallback = useRef(doNothing);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        const tick = () => savedCallback.current();
        const timeoutId = setTimeout(tick, delayMs);
        return () => clearTimeout(timeoutId);
    }, [delayMs]);
};

/**
 * Hook slouží pro uchování historie změn určité proměnné. Vrací historii hodnot.
 *
 * @param value Hodnota pro uložení do historie
 */
export const useValueHistory = <T>(value: T): T[] => {
    const historyRef = useRef<T[]>([]);
    const history = historyRef.current;

    if (!history.length || value !== history[0]) {
        historyRef.current.unshift(value);
    }

    return history.slice(1);
};

/**
 * Hook je ekvivalentem metody forceUpdate třídních komponent.
 * @return Funkce pro vynucení překreslení komponenty.
 * @see {@link https://github.com/CharlesStover/use-force-update/blob/master/src/use-force-update.ts}
 */
export const useForceUpdate = () => {
    const [, dispatch] = useState(Object.create(null));
    return useCallback(
        (): void => {
            dispatch(Object.create(null));
        },
        [dispatch],
    );
};

/**
 * Hook volající zadanou funkci při kliknutí mimo zadaný element.
 * Bere v potaz i eventy zrušených kliknutí _clickCancelled_.
 * @param refs Reference na elementy
 * @param onClickOutside Funkce volaná při kliknutí mimo element
 */
export const useClickOutsideWatcher = (refs: ReadonlyArray<RefObject<HTMLElement>>, onClickOutside: (event: Event) => void) => {
    useEffect(() => {
        let clickStartedFromWithin = false; // Slouží pro zjištění, jestli kliknutí bylo inicializováno uvnitř Dropdownu či mimo

        const handleClick = (event: Event) => {
            if (event.target instanceof Element && !clickStartedFromWithin) {
                const element: Element = event.target;
                if (!refs.some(ref => ref?.current?.contains(element))) {
                    onClickOutside(event);
                }
            }
        };

        const handleMouseDown = (event: Event) => {
            clickStartedFromWithin = refs.some(modalRef => modalRef?.current?.contains(event.target as Element) === true);
        };

        const addListeners = () => {
            document.addEventListener("mousedown", handleMouseDown, { capture: true });
            document.addEventListener("click", handleClick, { capture: true });
            document.addEventListener("clickCancelled", handleClick, { capture: true });
        };

        const removeListeners = () => {
            document.removeEventListener("mousedown", handleMouseDown, { capture: true });
            document.removeEventListener("click", handleClick, { capture: true });
            document.removeEventListener("clickCancelled", handleClick, { capture: true });
        };

        addListeners();
        return removeListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClickOutside, ...refs]);
};

/**
 * Hook volající zadanou funkci při kliku mimo trigger a content element.
 *
 * @param triggerRef Reference na trigger element.
 * @param contentRef Reference na modal content element.
 * @param onClickOutside Funkce volaná při kliknutí mimo element.
 * @param modalRoot Id pro modal root element (defaultně modalRoot id pro Multiweb).
 */
export const useClickOutsideModalWatcher = (triggerRef: RefObject<HTMLElement>, contentRef: RefObject<HTMLElement>, onClickOutside: (event: Event) => void, modalRoot: string = MODAL_ROOT_ID) => {
    const innerOnClickOutside = useCallback((event: Event) => {
        if (event.target instanceof Element) {
            const clickedElement: Element = event.target;

            /**
             * U zanořených modálů, které jsou renderované přes portal, nám  klasická kontrola nestačí.
             * Následující kontrola využívá toho, že popupy jsou v domu v tom pořadí,
             * jakém byly otevřeny.
             */
            if (contentRef.current?.closest(`#${modalRoot}`)) { // zkontrolujeme, že content je v modalRoot
                let popup: any = contentRef?.current?.closest(".popup");
                while (popup) {
                    if (popup.contains(clickedElement)) {
                        return;
                    }
                    popup = popup?.nextSibling;
                }
            }
            onClickOutside(event);
        }
    }, [contentRef, modalRoot, onClickOutside]);

    useClickOutsideWatcher([triggerRef, contentRef], innerOnClickOutside);
};

/**
 * Hook, který přidá MutationObserver. Lze využít například u komponent,
 * které potřebují reagovat na změnu elementů v DOMu.
 * @param ref Reference na element.
 * @param callback Metoda, která se zavolá při změnách DOMu.
 * @param options Volitené nastavení, lze pomocí něj určit, na jaké změny má MutationObserver reagovat.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver}
 */
export const useMutationObserver = <T extends HTMLElement>(ref: RefObject<T>, callback: MutationCallback, options?: MutationObserverInit) => {
    useEffect(() => {
        if (ref.current) {
            const observer = new MutationObserver(callback);
            observer.observe(ref.current, options);
        } else {
            logger.warn("MutationObserver couldn't be initialized with ref.current = undefined.");
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

/**
 * Hook pro použití intersection observeru. Dokáže zjistit, zda je element zobrazen (z části zobrazen - thresholds).
 *
 * @example Použití
 * import {useInView} from "@multiweb/reactUtils";
 * const Component = () => {
 *  const [ref, inView] = useInViewe();
 *  return (
 *      <div ref={ref}>
 *          Am I visible?
 *          {
 *              inView ? "Yes, I am." : "No, I do not."
 *          }
 *      </div>
 *  );
 * }
 *
 * @see{@link https://github.com/thebuilder/react-intersection-observer/blob/master/src/useInView.tsx}
 *
 * @param options nastavení pro
 */
export function useInView(options: IntersectionOptions = {}): InViewHookResponse {
    const ref = useRef<Element>();
    const [state, setState] = useState<IntersectionState>(initialIntersectionState);

    const setRef = useCallback(
        (node) => {
            if (ref.current) {
                unobserve(ref.current);
            }

            if (node) {
                observe(
                    node,
                    (inView, intersection) => {
                        setState({ inView, entry: intersection });
                        if (inView && options.triggerOnce) {
                            // If it should only trigger once, unobserve the element after it's inView
                            unobserve(node);
                        }
                    },
                    options,
                );
            }

            // Store a reference to the node, so we can unobserve it later
            ref.current = node;
        },
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [options.threshold, options.root, options.rootMargin, options.triggerOnce],
    );

    useEffect(() => {
        if (!ref.current && state !== initialIntersectionState && !options.triggerOnce) {
            // If we don't have a ref, then reset the state
            // This ensures we correctly reflect the current state - If you aren't observing anything, then nothing is inView
            setState(initialIntersectionState);
        }
    }, [state, options.triggerOnce]);

    return [setRef, state.inView, state.entry];
}

/**
 * Hook dělající skoro totéž, co {@link useEffect}.
 * Navíc řeší počáteční závislosti a tím předchází nechtěnému provolání efektu při mount.
 * (Viz https://github.com/facebook/react/issues/15873.)
 *
 * Příklad:
 * ```
 * const INITIAL_ARRAY = [1, 2, 3];
 * // Předpoklad: SomeComponent je vždy renderována jen s `myArray === INITIAL_ARRAY`.
 * const SomeComponent = ({myArray}) => {
 *     // someEffect1 se provolá 1x
 *     useEffect(someEffect1, [myArray]);
 *
 *     // someEffect2 se provolá 0x
 *     useEffectWithInitialDependencies(someEffect2, [myArray], INITIAL_ARRAY);
 *
 *     return (<div>...</div>);
 * };
 * ```
 *
 * @param effect Efekt, který se aplikuje, pokud je hodnota `dependencies` odlišná (shallow equal) od předchozího volání. (Toliko shoda s {@link useEffect}.)
 * Pokud jde o první volání `useEffectWithInitialDependencies` (tj. při `mount`), pak se `effect` provolá pouze, pokud `dependencies != initialDependencies` (shallow equal).
 * @param dependencies Závislosti, jejichž změna způsobí vyvolání `effect`. (Stejně jako u {@link useEffect}.
 * @param initialDependencies Uplatní se pouze při prvním provolání `useEffectWithInitialDependencies` (tj. při `mount`). Pak se vůči této hodnotě vyhodnocuje, zda-li došlo ke změně `dependencies`.
 */
export const useEffectWithInitialDependencies = <T>(effect: EffectCallback, dependencies: ReadonlyArray<T>, initialDependencies: ReadonlyArray<T>): void => {
    const firstCallRef = useRef(true);
    useEffect(() => {
        const canCallEffect = (!firstCallRef.current || !isArrayShallowEqual(dependencies, initialDependencies));
        firstCallRef.current = false;
        if (canCallEffect) {
            return effect();
        }
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
};

type UseStopPropagationEventHandlerParams = {
    /** Typ události, které listener naslouchá */
    readonly eventType: string;
    /** Funkce, která se zavolá při vyvolání události */
    readonly callback?: EventListener;
    /** Příznak, jestli se má na eventu zavolat _preventDefault_ */
    readonly preventDefault?: boolean;
    /** Příznak, že se nemá vyvolat event zrušení _[eventType]Cancelled_ */
    readonly dontTriggerCancelEvent?: boolean;
};

/** Pomocný typ {@link useStopPropagationEventListenerRef} pro uchování listeneru */
type ListenerData = {
    readonly element: Element;
    readonly listener: EventListener;
    readonly eventType: string;
};

/** Pomocná funkce pro {@link useStopPropagationEventListenerRef} pro smazání listeneru */
const clearListener = (ref: MutableRefObject<ListenerData | null>) => {
    const listenerInfo = ref.current;
    if (!listenerInfo) {
        return;
    }
    ref.current = null;
    const { element, eventType, listener } = listenerInfo;
    element.removeEventListener(eventType, listener);
};

/**
 * Hook, který vrátí RefCallback, který přes addEventListener naváže event přímo na konkrétní element a zruší propagaci eventu.
 *
 * Není potřeba od verze React 17.
 * @see{@link https://reactjs.org/blog/2020/08/10/react-v17-rc.html#changes-to-event-delegation}
 *
 * Díky tomu, že React 16 a níž navazuje eventy přes prop onClick apod. až na element window, tak se click listener
 * zavolá první na elementech, které jsou v nereactí části a až pak na elementech reactí části. Proto nezabírá
 * zavolání stopPropagation. Toto už je vyřešené v React v17, kde se tyto eventy navazují na prvek, nad kterým
 * se zavolá ReactDOM.render.
 *
 * @example Použití
 * clickListenerRef = useStopPropagationEventListenerRef({
 *      eventType: "click",
 *      callback: onDownloadRequested,
 *      preventDefault: true,
 *  });
 *
 *  return <a ref={clickListenerRef}>...</a>;
 */
export const useStopPropagationEventListenerRef = ({ eventType, callback, preventDefault, dontTriggerCancelEvent }: UseStopPropagationEventHandlerParams): RefCallback<Element | undefined> => {
    const listenerRef = useRef<ListenerData | null>(null);
    const elementRef = useRef<Element | undefined | null>();

    const handleEvent = useCallback<EventListener>(event => {
        if (preventDefault) {
            event.preventDefault();
        }

        if (dontTriggerCancelEvent) {
            event.stopPropagation();
        } else {
            stopPropagationNative(event);
        }

        callback?.(event);
    }, [callback, dontTriggerCancelEvent, preventDefault]);

    const handleElementSet = useCallback<RefCallback<Element | undefined>>(element => {
        elementRef.current = element;
        clearListener(listenerRef);

        if (element) {
            element.addEventListener(eventType, handleEvent);
            listenerRef.current = { element, eventType, listener: handleEvent };
        }
    }, [eventType, handleEvent]);

    // aktualizace při změně listeneru
    useEffect(() => {
        const element = elementRef.current;
        if (element) {
            handleElementSet(element);
        }
    }, [handleElementSet]);

    // odvázání listeneru při unmount
    useEffect(() => {
        return () => {
            clearListener(listenerRef);
        };
    }, []);

    return handleElementSet;
};

/**
 * Hook, který vrací callback memoizované funkce přidávající change eventu do HTML elementu a zároveň se stará i o její vymazání.
 * @template T HTML element, do kterého bude přidána eventa change
 * @param onJavaScriptChange callback na javascriptovou onChange eventu
 */
export const useJavaScriptChange = <T extends HTMLElement>(onJavaScriptChange?: OnJavaScriptChange) => {
    // nutno memoizovat, v opačném případě by docházelo k vytváření nové instance funkce a eventa by se nikdy z elementu nesmazala
    const handleOnJavaScriptChange = useCallback(e => {
        if (onJavaScriptChange) {
            onJavaScriptChange(e);
        }
    }, [onJavaScriptChange]);

    const ref: MutableRefObject<T | null> = useRef<T>(null);
    return useCallback(node => {
        if (ref.current) {
            // smaže eventu z poslední instance (nodu)
            ref.current.removeEventListener("change", handleOnJavaScriptChange);
        }

        if (node) {
            node.addEventListener("change", handleOnJavaScriptChange);
        }

        ref.current = node;
    }, [handleOnJavaScriptChange]);
};

type ElementScrollData = { width: number; height: number; scrollHeight: number; scrollTop: number };
/**
 * Hook vrací výšku, šířku, scrollovací šířku a aktuální pozici ve scrollovaní na předaném elementu.
 * Pokud element neexistuje a nebo ještě není vykreslen vrací nulové hodnoty.
 * @param elementRef Reference na element
 * @param dependencies Závíslosti
 */
export const useElementScrollData = <T extends HTMLElement = HTMLDivElement>(elementRef: RefObject<T>, dependencies: any[]): ElementScrollData => {
    const [elementSize, setElementSize] = useState<ElementScrollData>({
        width: 0,
        height: 0,
        scrollHeight: 0,
        scrollTop: 0,
    });

    // Vypínáme ho, protože lint nedokáže odvodit závislosti hooku uvnitř
    // řetězení funkcí v callbacku (obalení arrow funkce throttlem).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledHandleScroll = useCallback(throttle((e: Event) => {
        const node = e.target as HTMLElement;
        if (node) {
            setElementSize({
                width: node.offsetWidth || 0,
                height: node.offsetHeight || 0,
                scrollHeight: node.scrollHeight || 0,
                scrollTop: node.scrollTop || 0,
            });
        }
    }, SCROLL_INFO_THROTTLE_MS, { trailing: true, leading: true }), []);

    useEffect(() => {
        const node = elementRef?.current;
        if (node) {
            node.addEventListener("scroll", throttledHandleScroll, { passive: true });

            setElementSize({
                width: node.offsetWidth,
                height: node.offsetHeight,
                scrollHeight: node.scrollHeight,
                scrollTop: node.scrollTop,
            });
        }

        return () => {
            node?.removeEventListener("scroll", throttledHandleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elementRef, ...dependencies]);

    return elementSize;
};

/**
 * Hook volající zadanou funkci při přesunutí mimo zadaný element.
 * @param ref Reference na element
 * @param onMouseLeave Funkce volaná při přesunutí kurzoru mimo element
 * @param milliseconds Milisekundy určující prodlevu zavolání onMouseLeave
 */
export const useMouseLeaveWatcher = <T extends HTMLElement>(ref: RefObject<T>, onMouseLeave: () => void, milliseconds: number = 0) => {
    const timerRef = useRef<number>();

    useEffect(() => {
        const clearTimer = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };

        const handleLeave = () => {
            timerRef.current = self.setTimeout(onMouseLeave, milliseconds);
        };

        const refCurrent = ref.current;

        refCurrent?.addEventListener("mouseleave", handleLeave);
        refCurrent?.addEventListener("mouseenter", clearTimer);

        return () => {
            refCurrent?.removeEventListener("mouseleave", handleLeave);
            refCurrent?.removeEventListener("mouseenter", clearTimer);
            clearTimer();
        };
    }, [milliseconds, onMouseLeave, ref, timerRef]);
};

/**
 * Hook vracející novou instanci lazy-loading-loggeru
 * @param loggingSection Oblast pro kterou bude vrácený logger logovat
 */
export const useLogger = (loggingSection: LoggingSection) => {
    const logger = useMemo(() => LazyLoadingSectionLogger.create(loggingSection), [loggingSection]);
    return logger;
};

type ResizeFunction = ((size: number | undefined) => void) | undefined;

/**
 * Hook obalující ResizeObserver, který navíc doplňuje o throttle funkcionalitu
 * @param ref Reference na element
 * @param onWidthChange Funkce volaná při změně šířky
 * @param onHeightChange Funkce volaná při změně výšky
 * @param wait Čas v milisekundách, za kterou se funkce zavolají maximálně jednou
 */
export const useThrottleResizeObserver = <T extends HTMLElement>(ref: RefObject<T>, onWidthChange: ResizeFunction, onHeightChange: ResizeFunction, wait: number = 0) => {
    useLayoutEffect(() => {
        const handleResize = throttle(() => {
            if (ref.current) {
                onWidthChange && onWidthChange(ref.current ? ref.current.clientWidth : undefined);
                onHeightChange && onHeightChange(ref?.current ? ref?.current.clientHeight : undefined);
            }
        }, wait);
        const observer = new ResizeObserver(handleResize);
        if (ref?.current)
            observer.observe(ref.current);
        return () => observer.disconnect();
    }, [ref, onWidthChange, onHeightChange, wait]);
};

export const THROTTLE_RESIZE_OBSERVER_MILISECONDS = 200;

/**
 * Hook spočítá velikost fontu v procentech, tak aby se text vešel do svého parenta
 * @param ref Reference na element
 * @param maxFontSize maximální velikost fontu v procentech
 * @param value zobrazovaná hodnota
 * @returns
 */
export const useFitText = <T extends HTMLElement>(ref: RefObject<T>, refParent: RefObject<T>, maxFontSize: number, value: string | null | undefined) => {
    const [fontSize, setFontSize] = useState(maxFontSize);
    const prevWidth = useRef(0);
    const isEnlarging = useRef(false);

    const onWidthChange = (width: number | undefined) => {
        if (width && ref.current && refParent.current && value) {
            const valueWidth = ref.current.getBoundingClientRect().width;
            const parentWidth = refParent.current.getBoundingClientRect().width;

            if (prevWidth.current != parentWidth) {
                isEnlarging.current = (prevWidth.current - parentWidth < 0) && prevWidth.current > 0;
            }

            const ratio = parentWidth / valueWidth;

            if (ratio < 1 && isEnlarging.current) {
                isEnlarging.current = false;
            }
            if (!isEnlarging.current && ratio >= 1) {
                return;
            }
            const size = Math.min(Math.floor(fontSize * ratio), maxFontSize);

            prevWidth.current = parentWidth;

            setFontSize(size);
        }
    };

    useEffect(() => {
        isEnlarging.current = true;
    }, [value]);

    useThrottleResizeObserver(refParent, onWidthChange, undefined, THROTTLE_RESIZE_OBSERVER_MILISECONDS);

    return `${fontSize}%`;
};
